import { useCallback, RefObject, useState, useEffect } from 'react';

import { getCustomerUnifiedDialog } from '../../../../api';
import { usePagination } from '../../../../hooks/usePagination';
import { UnifiedDialogEntry } from '../../../../types';

import { useScrollRestoration } from './useScrollRestoration';

interface UseUnifiedDialogPaginationOptions {
  customerId: number | undefined;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

export const useUnifiedDialogPagination = ({
  customerId,
  scrollContainerRef,
}: UseUnifiedDialogPaginationOptions) => {
  const [entries, setEntries] = useState<UnifiedDialogEntry[]>([]);
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);

  const fetchPage = useCallback(
    async(page: number) => {
      if (!customerId) {
        return { results: [], next: null };
      }
      const response = await getCustomerUnifiedDialog(customerId, { page });
      // Capture last_read_at from page 1 response
      if (page === 1 && response.last_read_at !== undefined) {
        setLastReadAt(response.last_read_at ?? null);
      }
      return response;
    },
    [customerId],
  );

  const pagination = usePagination<UnifiedDialogEntry>({
    fetchPage,
    initialPage: 1,
  });

  // Reverse the data order for UnifiedDialog (prepend instead of append)
  useEffect(() => {
    // Create reversed copy of pagination data
    const reversedData = [...pagination.data].reverse();
    setEntries(reversedData);
  }, [pagination.data]);

  const { saveScrollPosition } = useScrollRestoration({
    containerRef: scrollContainerRef as RefObject<HTMLDivElement>,
    dataLength: entries.length,
    offsetHeight: 100,
  });

  const handleLoadMore = useCallback(async() => {
    if (!pagination.hasMore || pagination.isLoading) {
      return;
    }
    try {
      saveScrollPosition();
      await pagination.loadMore();
    } catch {
      // Silently handle pagination errors (404 on last page is expected)
    }
  }, [saveScrollPosition, pagination]);

  return {
    entries,
    lastReadAt,
    currentPage: pagination.currentPage,
    hasMore: pagination.hasMore,
    isLoading: pagination.isLoading,
    handleLoadMore,
    refetch: pagination.refetch,
  };
};
